import { Injectable } from '@angular/core';

import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from '@app/origami-api/services/base/service.base';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { Observable } from 'rxjs';
import { TopicField } from '@app/origami-api/utils/topic-utils';

export enum TeamPurpose {
  RESPONSIBLE_PARTIES = 1,
  QMS = 2,
}

export enum TeamGroupType {
  Root = 0,
  ProcessOwner_Assigned = 1,
  ProcessOwner_Escalated = 2,
  ProcessOwner_Informed = 3,
  Contributor_Assigned = 4,
  Contributor_Escalated = 5,
  Contributor_Informed = 6,
  Approver_Assigned = 7,
  Approver_Escalated = 8,
  Approver_Informed = 9,
}

export enum TeamMemberType {
  User = 1,
  Group = 2,
}

export enum TeamMemberAuthorityType {
  ALL,
  PROCESS_OWNER,
  CONTRIBUTOR,
  APPROVER,
}

export interface TeamListResponse {
  PageNumber: number;
  Records: TeamTopic[];
  TotalRowCount: number;
}

export interface TeamMemberListResponse {
  Records: TeamMember[];
  TotalRowCount: number;
  PageNumber: number;
}

export interface TeamMemberGroupListResponse {
  Result: TeamDetailsGroup[];
}

export interface TeamDocRecTypesListResponse {
  TotalRowCount: number;
  PageNumber: number;
  Records: TeamDocRecType[];
}

export interface TeamDocRecType {
  TeamId: number;
  TopicTypeId: number;
  TopicId: number;
  TopicName: string;
  Path: string;
  CategoryId: number;
  Category: string;
  UDID: string;
}

export interface TeamTopic {
  Fields: TopicField[];
  TeamId: number;
  Name: string;
  Type: TeamPurpose;
}

export interface TeamDetailsTopic {
  Fields: TopicField[];
  TeamId: number;
  Name: string;
  Type: TeamPurpose;
  TeamGroups: TeamDetailsGroup[];
  CreatedBy: number;
}

export interface TeamDetailsGroup {
  GroupType: number;
  GroupId: number;
  TotalMembersCount: number;
  UserMembersCount: number;
  GroupMembersCount: number;
}

export interface TeamMember {
  MemberId: number;
  MemberType: TeamMemberType;
  GroupType: TeamGroupType;
  Name: string;
  RoleLevel: number;
  EmailAddress: string;
  TeamGroups: TeamDetailsGroup[];
  Description?: string;
  PhoneNo?: string | null;
  MobileNo?: string | null;
  StatusId?: number;
  PasswordExpirationDate?: string | null;
  UserName: string;
}

export interface GetTeamListOptions {
  siteId: number;
  startingRow?: number;
  noOfRows?: number;
  orderByClause?: any[];
  searchQuery?: string;
}

export interface GetTeamMemberListOptions {
  siteId: number;
  teamId: number;
  groupTypes?: TeamGroupType[];
  startingRow?: number;
  noOfRows?: number;
  orderByClause?: any[];
  search?: string;
  expandGroups?: boolean;
}

export interface GetTeamMemberGroupsOptions {
  siteId: number;
  teamId: number;
  memberId: number;
}

export interface GetTeamDocRecTypesListOptions {
  siteId: number;
  teamId: number;
  startingRow?: number;
  noOfRows?: number;
  orderByClause?: any[];
  searchQuery?: string;
}

export interface UpdateTeamMemberPayload extends GetTeamMemberGroupsOptions {
  teamGroups: UpdateTeamMemberGroupPayload[];
}

export interface UpdateTeamMemberGroupPayload {
  GroupType: TeamGroupType;
  IsMember: boolean;
}

export interface DeleteTeamsOptions {
  siteId: number;
  teamIds: number[];
}

export interface AddTeamPayload {
  siteId: number;
  team: AddTeamPayloadData;
}

export interface UpdateTeamPayload {
  siteId: number;
  team: UpdateTeamPayloadData;
}

export interface SaveTeamMembersPayload {
  siteId: number;
  teamId: number;
  teamGroups: TeamGroupData[];
}

export interface SaveTeamDocRecTypesPayload {
  siteId: number;
  teamId: number;
  topicIds: number[];
}

export interface AddTeamPayloadData {
  TeamId?: number;
  Name: string;
  Type: TeamPurpose;
  TeamGroups?: TeamGroupData[];
  Fields?: TopicField[];
}

export interface UpdateTeamPayloadData {
  TeamId: number;
  Name?: string;
  Type?: TeamPurpose;
  TeamGroups?: TeamGroupData[];
  Fields?: TopicField[];
}

export interface TeamGroupData {
  GroupType: TeamGroupType;
  Members?: Partial<TeamMember>[];
}

export interface GetTeamOptions {
  siteId: number;
  teamId: number;
}

export const teamPurposeText: Record<TeamPurpose, string> = {
  [TeamPurpose.QMS]: 'QMS',
  [TeamPurpose.RESPONSIBLE_PARTIES]: 'Responsible Parties',
};

@Injectable()
export class TeamsService extends ServiceBase {
  serviceUrl = 'TeamManagementService.svc';

  public getTeamList(
    options: GetTeamListOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<TeamListResponse>> {
    return this.origamiPost('/GetTeams', options, null, silentErrorCodes, forceRequest);
  }

  public deleteTeams(options: DeleteTeamsOptions, silentErrorCodes?: ErrorCode[]): Observable<OrigamiHttpResponse> {
    silentErrorCodes = silentErrorCodes
      ? silentErrorCodes
      : [
          ErrorCode.FINISHED_WITH_MINOR_ERRORS,
          ErrorCode.RESPONSIBLE_DEPARTMENT_CANNOT_BE_DELETED_DUE_TO_ASSOCIATED_DOCUMENT_TYPES,
        ];
    return this.origamiPost('/DeleteTeams', options, null, silentErrorCodes, true);
  }

  public saveTeam(
    options: AddTeamPayload | UpdateTeamPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    silentErrorCodes = silentErrorCodes
      ? silentErrorCodes
      : [
          ErrorCode.FINISHED_WITH_MINOR_ERRORS,
          ErrorCode.DOCUMENT_TYPE_DOES_NOT_EXIST,
          ErrorCode.USER_DOES_NOT_EXIST_FOR_THE_SITE,
          ErrorCode.USER_GROUP_DOES_NOT_BELONG_TO_THE_SITE,
        ];
    return this.origamiPost('/SaveTeam', options, null, silentErrorCodes, true);
  }

  public getTeamDetailsTopic(
    options: GetTeamOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TeamDetailsTopic>> {
    return this.origamiPost('/GetTeam', options, null, silentErrorCodes, true);
  }

  public getTeamMemberList(
    options: GetTeamMemberListOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<TeamMemberListResponse>> {
    return this.origamiPost('/GetTeamMembers', options, null, silentErrorCodes, forceRequest);
  }

  public saveTeamMembers(
    options: SaveTeamMembersPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SaveTeamMembers', options, null, silentErrorCodes, true);
  }

  public addTeamMembers(
    options: SaveTeamMembersPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/AddTeamMembers', options, null, silentErrorCodes, true);
  }

  public removeTeamMembers(
    options: SaveTeamMembersPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/RemoveTeamMembers', options, null, silentErrorCodes, true);
  }

  public getTeamMemberGroups(
    options: GetTeamMemberGroupsOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<TeamMemberGroupListResponse>> {
    return this.origamiPost('/GetTeamGroupsByMember', options, null, silentErrorCodes, forceRequest);
  }

  public updateTeamMember(
    options: UpdateTeamMemberPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/UpdateTeamMember', options, null, silentErrorCodes, true);
  }

  public getTeamTopics(
    options: GetTeamDocRecTypesListOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<TeamDocRecTypesListResponse>> {
    return this.origamiPost('/GetTeamTopics', options, null, silentErrorCodes, forceRequest);
  }

  public addTeamTopics(
    options: SaveTeamDocRecTypesPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/AddTeamTopics', options, null, silentErrorCodes, true);
  }

  public removeTeamTopics(
    options: SaveTeamDocRecTypesPayload,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/RemoveTeamTopics', options, null, silentErrorCodes, true);
  }
}
